/**
 * Stats Row — Ball, Streak, Ochilgan lolalar / Mevali xurmolar.
 *
 * OPTIMIZED: Uses pre-calculated data from DB:
 *   - streak → habit.streak.current (from habit_streaks table, trigger-maintained)
 *   - bloom → daily_stats[].completion_rate >= 80 (from user_daily_stats, trigger-maintained)
 *   - score → completed logs count (from habit.logs) + streak
 *
 * NO calcAllDayStats() call — no 465-iteration loop.
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Zap, Flame, Flower2, TreePine } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import { useMonthlyHabits } from '@turnifly/shared/hooks'
import type { MonthlyHabitsResponse, MonthlyHabitItem } from '@turnifly/shared/types'

export const StatsRow: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id
  const { year, month } = useHomeStore()
  const { data } = useMonthlyHabits(userId, year, month)
  const isFemale = user?.gender === 'female'

  const { totalScore, maxStreak, bloomCount } = useMemo(() => {
    if (!data) return { totalScore: 0, maxStreak: 0, bloomCount: 0 }
    const parsed = data as MonthlyHabitsResponse
    const habits: MonthlyHabitItem[] = parsed.habits ?? []
    const dailyStats = parsed.daily_stats ?? []

    if (habits.length === 0) return { totalScore: 0, maxStreak: 0, bloomCount: 0 }

    // Score & streak — from pre-calculated habit.streak (DB trigger)
    let score = 0
    let streak = 0
    for (const h of habits) {
      const completedLogs = h.logs.filter((l) => l.completed).length
      score += completedLogs * 10 + h.streak.current * 5
      streak = Math.max(streak, h.streak.current)
    }

    // Bloom — from pre-calculated daily_stats (DB trigger)
    // Count days where completion_rate >= 80
    const today = new Date()
    const isCurMonth = today.getFullYear() === year && today.getMonth() + 1 === month
    const todayDay = isCurMonth ? today.getDate() : 31

    const blooms = dailyStats.filter((s) => {
      // Only count past/today days
      const dayNum = parseInt(s.stat_date.split('-')[2], 10)
      return dayNum <= todayDay && s.completion_rate >= 80 && s.total_habits > 0
    }).length

    return { totalScore: score, maxStreak: streak, bloomCount: blooms }
  }, [data, year, month])

  return (
    <div className="grid grid-cols-3 gap-2">
      <StatCard
        icon={<Zap size={15} fill="currentColor" />}
        iconColor="#6366F1"
        label={t('home.score')}
        value={totalScore.toLocaleString()}
      />
      <StatCard
        icon={<Flame size={15} fill="currentColor" />}
        iconColor="#F97316"
        label={t('home.streak')}
        value={t('home.streakDays', { count: maxStreak })}
      />
      <StatCard
        icon={isFemale
          ? <Flower2 size={15} fill="currentColor" strokeWidth={0} />
          : <TreePine size={15} fill="currentColor" strokeWidth={0} />}
        iconColor="#22C55E"
        label={isFemale ? t('home.garden') : t('home.orchard')}
        value={`${bloomCount} ta`}
      />
    </div>
  )
}

/* ── Single stat card ── */

interface StatCardProps {
  icon: React.ReactNode
  iconColor: string
  label: string
  value: string
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconColor, label, value }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5 flex items-center gap-2.5">
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
    >
      {icon}
    </div>
    <div className="flex flex-col min-w-0">
      <span className="text-[10px] text-text-hint/50 font-medium truncate">{label}</span>
      <span className="text-[15px] font-bold font-display text-text leading-tight">{value}</span>
    </div>
  </div>
)
